import React from "react";
import { Receipt } from "../services/receipt-service";

interface Props {
  data: Receipt[];
  loading?: boolean;
  isInactive?: boolean;
  startIndex?: number;
  categoryNameMap?: Record<number, string>;
  onEdit?: (item: Receipt) => void;
  onView?: (item: Receipt) => void;
  onChangeStatus?: (item: Receipt, newStatus: number) => void;
}

// Loading Skeleton Row
const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto" /></td>
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-48" /></td>
    <td className="py-4 px-4"><div className="h-6 bg-gray-200 rounded-full w-20 mx-auto" /></td>
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-24 ml-auto" /></td>
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-24" /></td>
    <td className="py-4 px-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
    <td className="py-4 px-4"><div className="flex gap-2 justify-center"><div className="h-8 w-8 bg-gray-200 rounded-lg" /><div className="h-8 w-8 bg-gray-200 rounded-lg" /></div></td>
  </tr>
);

// Type Badge Component
const TypeBadge: React.FC<{ type?: number }> = ({ type }) => {
  if (type === 1) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
        </svg>
        Thu
      </span>
    );
  }
  if (type === 2) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
        </svg>
        Chi
      </span>
    );
  }
  return <span className="text-gray-400">—</span>;
};

export const ReceiptTable: React.FC<Props> = ({
  data,
  loading,
  isInactive = false,
  startIndex = 0,
  categoryNameMap = {},
  onEdit,
  onView,
  onChangeStatus,
}) => {
  const withCategoryName = (it: Receipt): Receipt => ({
    ...it,
    category_name:
      it.category_name || categoryNameMap[Number(it.receipt_category_id)] || it.category_name,
  });

  const headers = [
    { label: "STT", className: "w-16 text-center" },
    { label: "Danh mục", className: "min-w-[180px]" },
    { label: "Nội dung", className: "min-w-[200px]" },
    { label: "Loại", className: "w-24 text-center" },
    { label: "Số tiền", className: "w-32 text-right" },
    { label: "Ngày hạch toán", className: "w-36" },
    { label: "Người tạo", className: "w-28" },
    { label: "Thao tác", className: "w-28 text-center" },
  ];

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {headers.map((head, i) => (
                <th
                  key={i}
                  className={`py-4 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${head.className}`}
                >
                  {head.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-medium">Không có phiếu nào</p>
                    <p className="text-gray-400 text-sm mt-1">Các phiếu thu chi sẽ hiển thị ở đây</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50/80 transition-colors group"
                >
                  {/* STT */}
                  <td className="py-4 px-4 text-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">
                      {startIndex + idx + 1}
                    </span>
                  </td>

                  {/* Danh mục */}
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        item.type === 1 ? "bg-green-100" : item.type === 2 ? "bg-red-100" : "bg-gray-100"
                      }`}>
                        <svg className={`w-5 h-5 ${
                          item.type === 1 ? "text-green-600" : item.type === 2 ? "text-red-600" : "text-gray-500"
                        }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate max-w-[200px]" title={item.category_name || categoryNameMap[Number(item.receipt_category_id)]}>
                          {item.category_name || categoryNameMap[Number(item.receipt_category_id)] || "Chưa phân loại"}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">#{item.document_number || item.id}</p>
                      </div>
                    </div>
                  </td>

                  {/* Nội dung */}
                  <td className="py-4 px-4">
                    <p className="text-gray-700 line-clamp-2 max-w-[250px]" title={item.note || ""}>
                      {item.note || <span className="text-gray-400 italic">Không có nội dung</span>}
                    </p>
                  </td>

                  {/* Loại phiếu */}
                  <td className="py-4 px-4 text-center">
                    <TypeBadge type={item.type} />
                  </td>

                  {/* Số tiền */}
                  <td className="py-4 px-4 text-right">
                    <span className={`font-bold text-base ${
                      item.type === 1 ? "text-green-600" : item.type === 2 ? "text-red-600" : "text-gray-700"
                    }`}>
                      {item.type === 1 ? "+" : item.type === 2 ? "-" : ""}
                      {Number(item.amount ?? 0).toLocaleString()}
                    </span>
                    <span className="text-gray-400 text-xs ml-1">đ</span>
                  </td>

                  {/* Ngày hạch toán */}
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">{item.transaction_date || "—"}</span>
                    </div>
                  </td>

                  {/* Người tạo */}
                  <td className="py-4 px-4">
                    {item.entry_type === 1 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-600 text-xs font-medium">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                        </svg>
                        Tự động
                      </span>
                    ) : (
                      <span className="text-gray-600 text-sm">{item.user_id || "—"}</span>
                    )}
                  </td>

                  {/* Thao tác */}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => onEdit?.(withCategoryName(item))}
                        aria-label="Sửa"
                        className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                        title="Chỉnh sửa"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      <button
                        type="button"
                        onClick={() => onView?.(withCategoryName(item))}
                        aria-label="Xem"
                        className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        title="Xem chi tiết"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
